import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import Link from 'next/link'
import { ButtonLink } from '@/components/generals/button-link'
import { CurrentYear } from '@/components/generals/current-year'
import { SocialLinks } from '@/components/generals/social-links'
import { Wordmark } from '@/components/generals/wordmark'
import { siteConfig } from '@/constants/site'

import {
    legalNavigation,
    mainNavigation,
    serviceNavigation,
    START_PROJECT_HREF
} from '@/constants/navigation'

interface FooterColumnProps {
    title: string
    items: Array<NavItem>
}

const FooterColumn: FC<FooterColumnProps> = ({ title, items }): ReactNode => {
    return (
        <div>
            <h2 className='font-mono text-xs uppercase tracking-[0.2em] text-electric-soft'>
                {title}
            </h2>

            <ul className='mt-5 space-y-3'>
                {items.map(item => (
                    <li key={item.href}>
                        <Link
                            href={item.href}
                            className='rounded-sm text-sm leading-snug text-muted-foreground outline-none transition-colors duration-300 hover:text-white focus-visible:text-white focus-visible:ring-2 focus-visible:ring-ring'
                        >
                            {item.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='relative isolate overflow-hidden border-t border-white/[0.07] bg-midnight-deep'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute -top-48 left-1/2 -z-10 h-96 w-[64rem] max-w-[200%] -translate-x-1/2 rounded-full bg-electric/10 blur-[120px]'
            />

            <div className='mx-auto max-w-7xl px-5 pt-20 sm:px-8 lg:pt-28'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <Wordmark />

                        <p className='font-display mt-8 max-w-md text-2xl font-extrabold uppercase leading-[1.05] tracking-[-0.01em] text-white sm:text-3xl'>
                            {siteConfig.statement}
                        </p>

                        <p className='mt-5 max-w-md leading-relaxed text-muted-foreground'>
                            A faith-led creative studio helping artists, musicians, and ambitious brands build digital experiences with purpose, clarity, and integrity.
                        </p>

                        <ButtonLink href={START_PROJECT_HREF} withArrow className='mt-8'>
                            Start a Project
                        </ButtonLink>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-3 lg:col-span-7'>
                        <FooterColumn title='Navigate' items={mainNavigation} />
                        <FooterColumn title='Services' items={serviceNavigation} />

                        <div>
                            <h2 className='font-mono text-xs uppercase tracking-[0.2em] text-electric-soft'>
                                Connect
                            </h2>

                            <a
                                href={`mailto:${siteConfig.email}`}
                                className='mt-5 inline-block break-all rounded-sm text-sm text-white outline-none transition-colors duration-300 hover:text-electric-soft focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                {siteConfig.email}
                            </a>

                            <p className='mt-2 text-sm text-muted-foreground'>
                                We usually reply {siteConfig.responseTime}.
                            </p>

                            <SocialLinks className='mt-6 flex-col items-start' />
                        </div>
                    </div>
                </div>

                <p
                    aria-hidden='true'
                    className='font-display mt-20 select-none text-[clamp(2.25rem,8.2vw,6.5rem)] font-extrabold uppercase leading-[0.88] tracking-[-0.03em] text-outline [overflow-wrap:anywhere]'
                >
                    {siteConfig.name}
                </p>

                <div className='mt-10 flex flex-col gap-4 border-t border-white/[0.07] py-8 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <CurrentYear /> {siteConfig.name}. All rights reserved.
                    </p>

                    <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                        {legalNavigation.map(item => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    className='rounded-sm outline-none transition-colors duration-300 hover:text-white focus-visible:text-white focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </footer>
    )
}
